import { useState, useCallback, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { ErrorList, Pagination, StatCard } from "./ErrorListHelpers";

const BUILD_TYPES = ["BUILD_ERROR", "code_error"];

export default function ErrorsTab() {
    const { t } = useTranslation();
    const [errors, setErrors] = useState([]);
    const [stats, setStats] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [filterType, setFilterType] = useState("");
    const [source, setSource] = useState("");

    const { call: fetchErrors, isLoading } = useBluelagoon({ method: "getIntelligenceErrors" });
    const { call: fetchStats } = useBluelagoon({ method: "getIntelligenceErrorStats" });

    const load = useCallback(async () => {
        const params = { page, limit: 20 };
        if (filterType) params.errorType = filterType;
        if (source) params.source = source;
        const [errRes, statsRes] = await Promise.all([
            fetchErrors(params),
            page === 1 && !filterType && !source ? fetchStats() : Promise.resolve(null),
        ]);
        if (errRes?.data) {
            setErrors(errRes.data.errors || []);
            setTotalPages(errRes.data.totalPages || 1);
            setTotal(errRes.data.total || 0);
        }
        if (statsRes?.data) setStats(statsRes.data);
    }, [fetchErrors, fetchStats, page, filterType, source]);

    useEffect(() => { load(); }, [load]);

    const handleFilterType = useCallback((type) => {
        setFilterType((prev) => (prev === type ? "" : type));
        setSource("");
        setPage(1);
    }, []);

    const handleSource = useCallback((s) => {
        setSource((prev) => (prev === s ? "" : s));
        setFilterType("");
        setPage(1);
    }, []);

    const buildCount = stats ? BUILD_TYPES.reduce((sum, t) => sum + (stats.byType?.[t] || 0), 0) : 0;
    const runtimeCount = stats ? (stats.totalOccurrences || 0) - buildCount : 0;
    const activeFilter = filterType || source;

    return (
        <div className="space-y-4 mt-4">
            <SourceFilter
                t={t} source={source} onSource={handleSource}
                buildCount={buildCount} runtimeCount={runtimeCount}
            />
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard label={t("admin.intelligence.errors.uniqueErrors")} value={stats.totalErrors} />
                    <StatCard label={t("admin.intelligence.errors.totalOccurrences")} value={stats.totalOccurrences} />
                    {Object.entries(stats.byType || {}).sort(([a], [b]) => {
                        const aB = BUILD_TYPES.includes(a) ? 0 : 1;
                        const bB = BUILD_TYPES.includes(b) ? 0 : 1;
                        return aB - bB;
                    }).map(([type, count]) => (
                        <StatCard
                            key={type} label={type} value={count}
                            active={filterType === type}
                            highlight={BUILD_TYPES.includes(type)}
                            onClick={() => handleFilterType(type)}
                        />
                    ))}
                </div>
            )}
            {activeFilter && (
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                        {t("admin.intelligence.errors.filterBy")}: <strong>{filterType || t(`admin.intelligence.errors.source_${source}`)}</strong>
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => { setFilterType(""); setSource(""); setPage(1); }}>
                        {t("common.clear")}
                    </Button>
                </div>
            )}
            <ErrorList errors={errors} isLoading={isLoading} t={t} />
            <Pagination page={page} totalPages={totalPages} total={total} setPage={setPage} t={t} />
        </div>
    );
}

function SourceFilter({ t, source, onSource, buildCount, runtimeCount }) {
    return (
        <div className="flex gap-2">
            <Button size="sm" variant={source === "build" ? "default" : "outline"} onClick={() => onSource("build")}>
                {t("admin.intelligence.errors.source_build")} ({buildCount})
            </Button>
            <Button size="sm" variant={source === "runtime" ? "default" : "outline"} onClick={() => onSource("runtime")}>
                {t("admin.intelligence.errors.source_runtime")} ({runtimeCount})
            </Button>
        </div>
    );
}
